import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Download, Loader2, Lock } from "lucide-react";

/**
 * Light email lead-gate for resource downloads. Captures name / work email /
 * company once (remembered for the session), records a lead, then hands back
 * control so the caller can start the actual download.
 */
export function BriefDownloadGate({ slug, docName, onClose, onUnlocked }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", company_website: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { i18n } = useTranslation();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const locale = (i18n.language || "en").slice(0, 2);
      await api.post("/leads", { ...form, kind: "brochure", source: `brief-download:${slug}`,
        message: `Downloaded resource: ${docName}`, locale });
      try { localStorage.setItem("erio_brief_unlocked", "1"); } catch { /* ignore */ }
      onUnlocked();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-lg border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ek-ink outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20";

  return (
    <Dialog open={!!slug} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="brief-gate">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Lock size={18} className="text-ek-purple" /> Just one step
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="-mt-1 text-sm text-ek-ink/70">
          Tell us where to send it and we&apos;ll unlock <strong>{docName}</strong> — plus any future downloads this visit.
        </DialogDescription>
        <form onSubmit={submit} className="mt-2 space-y-3.5">
          <input type="text" tabIndex={-1} autoComplete="off" value={form.company_website}
            onChange={set("company_website")} className="hidden" aria-hidden="true" />
          <div>
            <label className="mb-1 block text-xs font-medium text-ek-ink/70">Full name *</label>
            <input required value={form.name} onChange={set("name")} className={field}
              placeholder="Jane Smith" data-testid="gate-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ek-ink/70">Work email *</label>
            <input required type="email" value={form.email} onChange={set("email")} className={field}
              placeholder="jane@company.com" data-testid="gate-email" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ek-ink/70">Company *</label>
            <input required value={form.company} onChange={set("company")} className={field}
              placeholder="Acme plc" data-testid="gate-company" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="gate-error">{error}</p>}
          <button type="submit" disabled={loading}
            className="ek-btn flex w-full items-center justify-center gap-2 py-3 text-sm font-semibold"
            data-testid="gate-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={15} />}
            Unlock &amp; download
          </button>
          <p className="text-center text-[0.7rem] text-ek-ink/65">
            Commercial-in-Confidence. We&apos;ll only use your details to follow up on this enquiry.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
